"use client";

import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  Database,
  Loader2,
  Mail,
  Play,
  RotateCcw,
  Settings,
  Terminal,
  Webhook,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";

type NodeStatus = "idle" | "running" | "complete" | "error" | "retrying";

interface WorkflowNode {
  id: string;
  type: "trigger" | "action" | "logic";
  icon: React.ElementType;
  label: string;
  status: NodeStatus;
  duration?: string;
  output?: string;
}

interface LogEntry {
  timestamp: string;
  message: string;
  type: "info" | "success" | "error" | "warning";
  nodeId?: string;
}

const initialNodes: WorkflowNode[] = [
  {
    id: "webhook",
    type: "trigger",
    icon: Webhook,
    label: "Webhook Received",
    status: "idle",
  },
  {
    id: "logic",
    type: "logic",
    icon: Settings,
    label: "Data Transformation",
    status: "idle",
  },
  {
    id: "enrich",
    type: "action",
    icon: Database,
    label: "Enrich Contact",
    status: "idle",
  },
  {
    id: "notify",
    type: "action",
    icon: Mail,
    label: "Send to Slack",
    status: "idle",
  },
];

export function WorkflowDemo() {
  const [nodes, setNodes] = useState<WorkflowNode[]>(initialNodes);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [runId, setRunId] = useState(1247);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showRetry, setShowRetry] = useState(false);
  const [activeNodeIndex, setActiveNodeIndex] = useState(-1);

  const addLog = useCallback((entry: Omit<LogEntry, "timestamp">) => {
    const now = new Date();
    const timestamp = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}:${now.getSeconds().toString().padStart(2, "0")}.${now.getMilliseconds().toString().padStart(3, "0")}`;
    setLogs((prev) => [...prev, { ...entry, timestamp }]);
  }, []);

  const updateNode = useCallback(
    (id: string, updates: Partial<WorkflowNode>) => {
      setNodes((prev) =>
        prev.map((n) => (n.id === id ? { ...n, ...updates } : n)),
      );
    },
    [],
  );

  const resetDemo = useCallback(() => {
    setNodes(initialNodes);
    setLogs([]);
    setElapsedTime(0);
    setShowRetry(false);
    setActiveNodeIndex(-1);
  }, []);

  const runWorkflow = useCallback(async () => {
    if (isRunning) return;

    resetDemo();
    setIsRunning(true);
    setRunId((prev) => prev + 1);

    const startTime = Date.now();
    const timerInterval = setInterval(() => {
      setElapsedTime(Date.now() - startTime);
    }, 50);

    // Node 1: Webhook
    setActiveNodeIndex(0);
    addLog({
      message: "Workflow triggered via webhook",
      type: "info",
      nodeId: "webhook",
    });
    updateNode("webhook", { status: "running" });

    await new Promise((r) => setTimeout(r, 700));
    updateNode("webhook", {
      status: "complete",
      duration: "124ms",
      output: '{"email": "lead@company.com"}',
    });
    addLog({
      message: "Payload validated · 124ms",
      type: "success",
      nodeId: "webhook",
    });

    // Node 2: Logic
    await new Promise((r) => setTimeout(r, 400));
    setActiveNodeIndex(1);
    addLog({
      message: "Applying transformation logic...",
      type: "info",
      nodeId: "logic",
    });
    updateNode("logic", { status: "running" });

    await new Promise((r) => setTimeout(r, 800));
    updateNode("logic", {
      status: "complete",
      duration: "450ms",
      output: '{"status": "processed"}',
    });
    addLog({
      message: "Data transformed · 450ms",
      type: "success",
      nodeId: "logic",
    });

    // Node 3: Enrich
    await new Promise((r) => setTimeout(r, 400));
    setActiveNodeIndex(2);
    addLog({
      message: "Starting contact enrichment...",
      type: "info",
      nodeId: "enrich",
    });
    updateNode("enrich", { status: "running" });

    await new Promise((r) => setTimeout(r, 1000));
    updateNode("enrich", {
      status: "complete",
      duration: "892ms",
      output: '{"company_size": "50-200"}',
    });
    addLog({
      message: "Clearbit enrichment complete · 892ms",
      type: "success",
      nodeId: "enrich",
    });

    // Node 4: Notify (with failure + retry)
    await new Promise((r) => setTimeout(r, 400));
    setActiveNodeIndex(3);
    addLog({
      message: "Sending Slack notification...",
      type: "info",
      nodeId: "notify",
    });
    updateNode("notify", { status: "running" });

    await new Promise((r) => setTimeout(r, 500));
    updateNode("notify", { status: "error" });
    addLog({
      message: "Slack API timeout (attempt 1/3)",
      type: "error",
      nodeId: "notify",
    });
    setShowRetry(true);

    await new Promise((r) => setTimeout(r, 900));
    addLog({
      message: "Retrying with exponential backoff...",
      type: "warning",
      nodeId: "notify",
    });
    updateNode("notify", { status: "retrying" });

    await new Promise((r) => setTimeout(r, 700));
    updateNode("notify", { status: "complete", duration: "1.8s" });
    addLog({
      message: "Notification sent successfully · 1.8s",
      type: "success",
      nodeId: "notify",
    });
    setShowRetry(false);

    await new Promise((r) => setTimeout(r, 300));
    addLog({ message: "Workflow completed successfully", type: "success" });
    setActiveNodeIndex(-1);

    clearInterval(timerInterval);
    setElapsedTime(Date.now() - startTime);
    setIsRunning(false);
  }, [isRunning, addLog, updateNode, resetDemo]);

  useEffect(() => {
    const timeout = setTimeout(runWorkflow, 2000);
    return () => clearTimeout(timeout);
  }, []);

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getStatusIcon = (status: NodeStatus) => {
    switch (status) {
      case "complete":
        return <CheckCircle2 className="w-4 h-4 text-success" />;
      case "running":
        return <Loader2 className="w-4 h-4 text-primary animate-spin" />;
      case "error":
        return <AlertTriangle className="w-4 h-4 text-destructive" />;
      case "retrying":
        return <RotateCcw className="w-4 h-4 text-warning animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getLogTypeColor = (type: LogEntry["type"]) => {
    switch (type) {
      case "success":
        return "text-success";
      case "error":
        return "text-destructive";
      case "warning":
        return "text-warning";
      default:
        return "text-muted-foreground";
    }
  };

  const completedSteps = nodes.filter((n) => n.status === "complete").length;
  const progress = (completedSteps / nodes.length) * 100;

  return (
    <div className="grid lg:grid-cols-5 lg:min-h-[440px] overflow-hidden rounded-xl border border-border/50">
      {/* Workflow Canvas */}
      <div className="lg:col-span-3 p-4 sm:p-5 lg:p-6 bg-gradient-to-br from-card via-card to-muted/20 relative">
        {/* Window chrome */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div className="flex items-center gap-3">
            <div className="h-4 w-px bg-border hidden sm:block" />
            <span className="text-[10px] sm:text-xs font-medium text-muted-foreground font-mono">
              lead-capture.workflow
            </span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={runWorkflow}
            disabled={isRunning}
            className="h-7 px-2 sm:px-3 text-[10px] sm:text-xs gap-1.5 hover:bg-primary/10 hover:text-primary"
          >
            {isRunning ? (
              <>
              <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
              Running...
              </>
              ) : (
                <>
                <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> 
                Run
                </>
                )}
          </Button>
        </div>

        {/* Nodes with beam connectors */}
        <div className="space-y-2">
          {nodes.map((node, index) => (
            <div key={node.id}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`
                  relative flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg border transition-all duration-500
                  ${node.status === "running" ? "bg-primary/10 border-primary/50 shadow-lg shadow-primary/20" : ""}
                  ${node.status === "complete" ? "bg-success/5 border-success/30" : ""}
                  ${node.status === "error" ? "bg-destructive/10 border-destructive/50" : ""}
                  ${node.status === "retrying" ? "bg-warning/10 border-warning/50" : ""}
                  ${node.status === "idle" ? "bg-card/80 border-border/50" : ""}
                `}
              >
                {/* Icon with glow */}
                <div
                  className={`
                  relative w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300
                    ${node.type === "trigger" ? "bg-primary/20" : node.type === "logic" ? "bg-amber-500/20" : "bg-muted/80"}
                    ${node.status === "running" ? "shadow-lg shadow-primary/30" : ""}
                  `}
                >
                  <node.icon
                    className={`w-4 h-4 sm:w-5 sm:h-5 ${node.type === "trigger" ? "text-primary" : node.type === "logic" ? "text-amber-500" : "text-foreground/70"}`}
                  />
                  {node.status === "running" && (
                    <motion.div
                      className={`absolute inset-0 rounded-lg ${node.type === "logic" ? "bg-amber-500/20" : "bg-primary/20"}`}
                      animate={{ opacity: [0.5, 0, 0.5] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span
                      className={`text-[8px] sm:text-[10px] font-semibold uppercase tracking-wider ${node.type === "logic" ? "text-amber-500" : "text-muted-foreground"}`}
                    >
                      {node.type}
                    </span>
                    {node.duration && (
                      <span className="text-[8px] sm:text-[10px] font-mono text-primary/80 bg-primary/10 px-1.5 py-0.5 rounded">
                        {node.duration}
                      </span>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-foreground truncate">
                    {node.label}
                  </p>
                </div>

                {/* Status */}
                <div className="flex-shrink-0">
                  {getStatusIcon(node.status)}
                </div>

                {/* Running shimmer */}
                {node.status === "running" && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent"
                    animate={{ x: ["-100%", "100%"] }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                )}
              </motion.div>

              {/* Beam connector */}
              {index < nodes.length - 1 && (
                <div className="flex justify-center h-4 sm:h-6 relative">
                  <div className="w-0.5 h-full bg-border/50 relative overflow-hidden">
                    <AnimatePresence>
                      {activeNodeIndex === index && (
                        <motion.div
                          initial={{ y: "-100%" }}
                          animate={{ y: "100%" }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.5, ease: "easeInOut" }}
                          className="absolute inset-x-0 h-1/2 bg-gradient-to-b from-primary via-primary to-transparent"
                        />
                      )}
                    </AnimatePresence>
                    {nodes[index + 1].status !== "idle" && (
                      <div className="absolute inset-0 bg-primary/50" />
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Execution Panel */}
      <div className="lg:col-span-2 border-t lg:border-t-0 lg:border-l border-border flex flex-col bg-background/50 backdrop-blur-sm">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 sm:py-3 border-b border-border bg-card/50">
          <div className="flex items-center gap-2">
            <Terminal className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
            <span className="text-[10px] sm:text-xs font-semibold text-foreground font-mono">
              Run #{runId}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <AnimatePresence>
              {showRetry && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded text-[9px] sm:text-[10px] font-medium bg-warning/20 text-warning border border-warning/30"
                >
                  <RotateCcw className="w-2.5 h-2.5 sm:w-3 sm:h-3 animate-spin" />
                  Retrying
                </motion.span>
              )}
            </AnimatePresence>
            <span
              className={`
              inline-flex items-center gap-1 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[9px] sm:text-[10px] font-semibold
              ${isRunning ? "bg-primary/20 text-primary border border-primary/30" : progress === 100 ? "bg-success/20 text-success border border-success/30" : "bg-muted text-muted-foreground border border-border"}
            `}
            >
              {isRunning && (
                <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-current animate-pulse" />
              )}
              {isRunning ? "Running" : progress === 100 ? "Complete" : "Idle"}
            </span>
          </div>
        </div>

        {/* Logs */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-1 max-h-[200px] sm:max-h-[280px] font-mono text-[10px] sm:text-xs bg-card/30">
          {logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-muted-foreground">
              <Terminal className="w-6 h-6 sm:w-8 sm:h-8 mb-2 opacity-30" />
              <p className="text-[10px] sm:text-xs">
                Click "Run" to execute workflow
              </p>
            </div>
          ) : (
            logs.map((log, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2 }}
                className="flex items-start gap-2"
              >
                <span className="text-muted-foreground/50 flex-shrink-0 w-[60px] sm:w-[72px]">
                  {log.timestamp}
                </span>
                <ChevronRight className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-muted-foreground/30 flex-shrink-0 mt-0.5" />
                <span className={getLogTypeColor(log.type)}>{log.message}</span>
              </motion.div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 sm:py-3 border-t border-border bg-card/50">
          <div className="flex items-center justify-between text-[9px] sm:text-[10px] text-muted-foreground mb-1.5 sm:mb-2">
            <span className="font-medium">Progress</span>
            <span className="font-mono">{formatTime(elapsedTime)}</span>
          </div>
          <div className="h-1 sm:h-1.5 rounded-full bg-muted overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-primary to-primary/80"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
